/**
 * Presentational pieces of the seller's order page: the header with the
 * order's progress, and consistent section titles. Behaviour stays in
 * `OrderDetailPage`; these only draw.
 */
import { ArrowLeft, Check, CircleDot, PackageCheck, Truck, X, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

/** The forward path a package takes, as the seller sees it. */
const STEPS: Array<{ key: string; label: string; icon: LucideIcon }> = [
  { key: 'PLACED', label: 'New', icon: CircleDot },
  { key: 'CONFIRMED', label: 'Accepted', icon: Check },
  { key: 'PACKED', label: 'Packed', icon: PackageCheck },
  { key: 'SHIPPED', label: 'Shipped', icon: Truck },
  { key: 'DELIVERED', label: 'Delivered', icon: Check },
];

const STEP_INDEX: Record<string, number> = {
  PENDING_PAYMENT: -1,
  PLACED: 0,
  CONFIRMED: 1,
  PACKED: 2,
  SHIPPED: 3,
  DELIVERED: 4,
  CLOSED: 4,
  RETURN_REQUESTED: 4,
  RETURN_APPROVED: 4,
  RETURN_RECEIVED: 4,
  REFUNDED: 4,
};

/** Status pill colours: waiting (amber), moving (sky), done (emerald), stopped (rose). */
// eslint-disable-next-line react-refresh/only-export-components -- a colour helper shared with the page
export function statusPillClass(status: string): string {
  if (status === 'CANCELLED') return 'bg-rose-50 text-rose-700 ring-rose-200';
  if (status === 'PLACED' || status === 'PENDING_PAYMENT' || status.startsWith('RETURN'))
    return 'bg-amber-50 text-amber-800 ring-amber-200';
  if (status === 'DELIVERED' || status === 'CLOSED')
    return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
  return 'bg-sky-50 text-sky-700 ring-sky-200';
}

export function OrderHero({
  orderNumber,
  placedAt,
  status,
  statusLabel,
  itemCount,
  totalLabel,
  paymentLabel,
  onBack,
}: {
  orderNumber: string;
  placedAt: string;
  status: string;
  statusLabel: string;
  itemCount: number;
  totalLabel: string;
  paymentLabel: string;
  onBack: () => void;
}) {
  const current = STEP_INDEX[status] ?? -1;
  const cancelled = status === 'CANCELLED';

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
      <div className="relative bg-gradient-to-r from-[#a8161b] via-[#b3261e] to-[#c2410c] px-5 py-5 text-white sm:px-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <button
              type="button"
              onClick={onBack}
              className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-white/15 transition-colors hover:bg-white/25"
              aria-label="Back to orders"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/70">
                Order
              </p>
              <h1 className="font-mono text-xl font-black tracking-tight sm:text-2xl">
                #{orderNumber}
              </h1>
              <p className="mt-0.5 text-[13px] text-white/80">Placed {placedAt}</p>
            </div>
          </div>
          <span
            className={`rounded-full px-3.5 py-1.5 text-[12.5px] font-extrabold ring-1 ring-inset ${statusPillClass(status)}`}
          >
            {statusLabel}
          </span>
        </div>

        <dl className="mt-5 grid grid-cols-3 gap-3 border-t border-white/15 pt-4 text-[12px]">
          {[
            ['Items', String(itemCount)],
            ['Package value', totalLabel],
            ['Payment', paymentLabel],
          ].map(([k, v]) => (
            <div key={k}>
              <dt className="font-semibold uppercase tracking-wider text-white/60">{k}</dt>
              <dd className="mt-0.5 text-[15px] font-extrabold">{v}</dd>
            </div>
          ))}
        </dl>
      </div>

      {/* Progress */}
      <div className="px-5 py-4 sm:px-6">
        {cancelled ? (
          <p className="flex items-center gap-2 text-[13px] font-bold text-rose-700">
            <X className="h-4 w-4" /> This order was cancelled.
          </p>
        ) : (
          <ol className="flex items-center">
            {STEPS.map((s, i) => {
              const done = i < current || (i === current && i === STEPS.length - 1);
              const active = i === current && !done;
              const Icon = s.icon;
              return (
                <li key={s.key} className="flex flex-1 items-center last:flex-none">
                  <div className="flex flex-col items-center gap-1">
                    <span
                      className={`grid h-8 w-8 place-items-center rounded-full ring-2 transition-colors ${
                        done
                          ? 'bg-emerald-500 text-white ring-emerald-500'
                          : active
                            ? 'bg-amber-50 text-amber-700 ring-amber-400'
                            : 'bg-white text-slate-300 ring-slate-200'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <span
                      className={`text-[11px] font-bold ${
                        done ? 'text-emerald-700' : active ? 'text-amber-800' : 'text-slate-400'
                      }`}
                    >
                      {s.label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <span
                      className={`mx-1.5 mb-5 h-0.5 flex-1 rounded-full ${
                        i < current ? 'bg-emerald-400' : 'bg-slate-200'
                      }`}
                    />
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}

/** A card title with an icon chip, used by every section on the page. */
export function SectionTitle({
  icon: Icon,
  children,
  right,
}: {
  icon: LucideIcon;
  children: ReactNode;
  right?: ReactNode;
}) {
  return (
    <div className="mb-4 flex items-center justify-between gap-3">
      <h2 className="flex items-center gap-2.5 text-[16px] font-black text-slate-900">
        <span className="bg-[#a8161b]/8 grid h-8 w-8 place-items-center rounded-lg text-[#a8161b]">
          <Icon className="h-4 w-4" />
        </span>
        {children}
      </h2>
      {right}
    </div>
  );
}
